// ============================================================================
// ELIGIBILITY CRITERIA SERVICE — Phase E
// ============================================================================

import { get, post, del } from '../client/axiosClient';
import { eligibilityCriteriaEndpoints, ELIGIBILITY_CRITERIA_BASE_PATH } from '../endpoints/eligibilityCriteria.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, EmptyResponse, PaginatedResponse, ID } from '../types';
import type {
  EligibilityCriteriaListItem,
  EligibilityCriteriaSearchRequest,
  EligibilityCriteriaSet,
  EligibilityField,
  FindEligibleResult,
  SaveEligibilityCriteriaRequest,
} from '../types/eligibilityCriteria.types';

class EligibilityCriteriaService {
  async getAll(params?: EligibilityCriteriaSearchRequest): Promise<PaginatedResponse<EligibilityCriteriaListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(ELIGIBILITY_CRITERIA_BASE_PATH, params as Record<string, any>)
      : eligibilityCriteriaEndpoints.list();
    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 25,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getFields(): Promise<EligibilityField[]> {
    const res = await get<ApiResponse<EligibilityField[]>>(eligibilityCriteriaEndpoints.fields());
    return res.data?.data ?? [];
  }

  async getByCaseType(caseTypeCode: string, includeHistory = false): Promise<EligibilityCriteriaSet[]> {
    const res = await get<ApiResponse<EligibilityCriteriaSet[]>>(
      eligibilityCriteriaEndpoints.byCaseType(caseTypeCode, includeHistory),
    );
    return res.data?.data ?? [];
  }

  async getById(id: ID): Promise<EligibilityCriteriaSet | null> {
    const res = await get<ApiResponse<EligibilityCriteriaSet>>(eligibilityCriteriaEndpoints.getById(id));
    return res.data?.data ?? null;
  }

  async save(request: SaveEligibilityCriteriaRequest): Promise<EligibilityCriteriaSet> {
    const res = await post<ApiResponse<EligibilityCriteriaSet>>(eligibilityCriteriaEndpoints.save(), request);
    return res.data.data!;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const res = await del<EmptyResponse>(eligibilityCriteriaEndpoints.delete(id));
    return res.data;
  }

  async findEligible(id: ID): Promise<FindEligibleResult> {
    const res = await post<ApiResponse<FindEligibleResult>>(eligibilityCriteriaEndpoints.findEligible(id), {});
    return res.data.data!;
  }
}

export const eligibilityCriteriaService = new EligibilityCriteriaService();
export default eligibilityCriteriaService;
